import { Fragment, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { Button, Checkbox } from '@upmc/ims.component-library';
import axiosInstance from '../../../services/axios/axios';
import { UserContext } from 'context/User/UserContext';
import { getLegacyPath } from 'routes/_utils';
import { WrapImsPacket } from '../../../store/utility';
import Card, { Actions } from '../../../components/UI/MDC/Card/Card';
import {
	Headline6,
	Caption,
	Subtitle2,
	Body1,
} from '../../../components/UI/MDC/Typography/Typography';
import List, {
	ListItem,
	MDCListItemText,
} from '../../../components/UI/MDC/List/List';
import { RequestTypes } from '../../../constants/enums';
import { CSS_CLASSES, OBJ_ACCOUNT_TYPES } from './_constants';
import classes from './AddServiceAccount.module.scss';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Base from '../../Base/Base';
import { HomeButton } from '../../../components/UI/Input/Button/index';
import { raiseErrorBanner } from '../../../components/MessageDispatch/messageDispatch';
import { deepCloneObject } from '../../../store/utility';

const AddServiceAccount = () => {
	const [sAccountTypeSelection, setAccountTypeSelection] = useState(); // Key,Value object corresponding to account type ID, value.
	const [sServiceNowGroupSelection, setServiceNowGroupSelection] = useState();
	const [sAreOwnersValid, setAreOwnersValid] = useState(false);
	const [sArrAccountType, setArrAccountType] = useState([]);
	const [sArrServiceNowGroup, setArrServiceNowGroup] = useState([]);
	const [sArrApplications, setArrApplications] = useState([]); // array of Key,Value objects corresponding to application ID, name.
	const [sArrApplicationSelections, setArrApplicationSelections] = useState(
		[],
	);
	const [sArrOwners, setArrOwners] = useState([]); // array of objects including base property "Data"
	const [sIsLoading, setIsLoading] = useState(false); // boolean for validation loading animation
	const [sIsSubmitting, setIsSubmitting] = useState(false); // boolean for submission loading animation
	const [sJustification, setJustification] = useState([]);
	const [sValidJustification, setValidJustification] = useState(true);
	const [sOther, setOther] = useState();
	const [sValidOther, setValidOther] = useState(true);
	const [sValidServiceNowGroup, setValidServiceNowGroup] = useState(true);
	const [sRequestTrackingNumber, setRequestTrackingNumber] = useState();
	const [sWasSubmitted, setWasSubmitted] = useState(false); // boolean for tracking validation
	const [sOpenNoticeModal, setOpenNoticeModal] = useState(false);
	const [sOpenOnLoadModal, setOpenOnLoadModal] = useState(true);
	const [sOpenEmailWarningModal, setOpenEmailWarningModal] = useState(false);
	const [sExistingUserAccount, setExistingUserAccount] = useState();
	const dispatch = useDispatch();

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.userProfile;

	//#region state parameters
	/**
	 * User specified parameter(s) from state.
	 */
	const location = useLocation();
	const requestType = location.state.REQUEST_TYPE;
	const requestorESOFIID = location.state.requestorESOFIID;
	const requesteeFamiliarESOID = location.state.Familiar_ESO_ID;
	const UserName = location.state.User;
	//#endregion

	/**
	 * Effect on component mount. Queries the current user's information and performs initial page population accordingly.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		axiosInstance
			.post(
				'api/AddServiceAccount/Populate',
				WrapImsPacket(requestorESOFIID),
			)
			.then(response => {
				const payload = response.data;

				// Populating account type dropdown
				let arrAccountType = payload.AccountTypes
					? Object.keys(payload.AccountTypes).map(key => {
							return {
								key: payload.AccountTypes[key].Key,
								value: payload.AccountTypes[key].Value,
								description:
									payload.AccountTypes[key].Description,
								notice: payload.AccountTypes[key].Notice,
							};
						})
					: [];
				setArrAccountType(arrAccountType);

				// Populating ServiceNow Support Group dropdown
				let arrServiceNowGroup = payload.CherwellGroups
					? Object.keys(payload.CherwellGroups)
							.map(key => {
								return {
									key: payload.CherwellGroups[key].Key,
									value: payload.CherwellGroups[key].Value,
								};
							})
							.sort((firstEl, secondEl) => {
								const valueFirstEl =
									firstEl.value.toLowerCase();
								const valueSecondEl =
									secondEl.value.toLowerCase();
								if (valueFirstEl < valueSecondEl) {
									return -1;
								}
								if (valueFirstEl > valueSecondEl) {
									return 1;
								}
								return 0;
							})
					: [];
				setArrServiceNowGroup(arrServiceNowGroup);
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Effect on local state change. Resets validation on state change.
	 * Does not need to check value of local state, value of local state considered when rendering validation message.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		if (sWasSubmitted) {
			setWasSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sAccountTypeSelection, sArrApplicationSelections, sArrOwners]);

	/**
	 * Account type change handler. Retrieves application(s) associated with selected account type.
	 * @param {Object} accountTypeSelection Nullable Key,Value pair for account type selection.
	 * @returns void.
	 */
	const onAccountTypeChangeHandler = async accountTypeSelection => {
		setAccountTypeSelection(accountTypeSelection);
		setArrApplicationSelections([]);
		setArrOwners([]);
		setOther();
		setServiceNowGroupSelection();
		axiosInstance
			.post(
				'api/AddServiceAccount/Applications',
				WrapImsPacket(
					accountTypeSelection ? accountTypeSelection.Key : '',
				),
			)
			.then(response => {
				setArrApplications(
					accountTypeSelection?.Key ===
						OBJ_ACCOUNT_TYPES.SHARED_CALENDAR
						? response.data.payload?.filter(
								app => app.Key !== '1482',
							)
						: response.data.payload,
				);
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});

		/*
		Account types that require an associated ServiceNow support group
		(45) Other Service Account
		(5) Service to Run Under
		(75) Clinical Service Account
		(79) Universal PC Autologin
		*/
		if (
			accountTypeSelection &&
			(accountTypeSelection.Key === '45' ||
				accountTypeSelection.Key === '5' ||
				accountTypeSelection.Key === '75' ||
				accountTypeSelection.Key === '79' ||
				accountTypeSelection.Key === '83' ||
				accountTypeSelection.Key === '84')
		) {
			setValidServiceNowGroup(false);
		} else {
			setValidServiceNowGroup(true);
		}
		if (accountTypeSelection) {
			await onValidateHandler(accountTypeSelection);
		}
	};

	/**
	 * Account type change handler. Retrieves application(s) associated with selected account type.
	 * @param {Object} accountTypeSelection Nullable Key,Value pair for account type selection.
	 * @returns void.
	 */
	const onServiceNowGroupChangeHandler = serviceNowGroupSelection => {
		setServiceNowGroupSelection(serviceNowGroupSelection);
		if (serviceNowGroupSelection === null) {
			setValidServiceNowGroup(false);
		} else {
			setValidServiceNowGroup(true);
		}
	};

	/**
	 * Maintains an array of unique item selections. Pushes to (or) splices from array depending on existence.
	 * @param {Object} itemSelected object equivalent of selected item.
	 * @param {Array} itemSelections array of existing unique item selection objects.
	 * @returns updated array of unique item selections.
	 * @example
	 * // returns [{foo: true}, {foo: false}]
	 * onCheckBoxSelect({foo: false}, [{foo: true}])
	 * // returns [{foo: true}]
	 * onCheckBoxSelect({foo: false}, [{foo: true}, {foo: false}])
	 */
	const onCheckBoxSelect = (itemSelected, itemSelections) => {
		let itemSelectionsCopy = [...itemSelections];

		if (itemSelections.includes(itemSelected)) {
			itemSelectionsCopy.splice(itemSelections.indexOf(itemSelected), 1);
		} else {
			itemSelectionsCopy.push(itemSelected);
		}

		if (sAccountTypeSelection?.Key === OBJ_ACCOUNT_TYPES.SHARED_CALENDAR) {
			itemSelectionsCopy =
				onSharedCalendarCheckBoxSelect(itemSelectionsCopy);
		}

		return itemSelectionsCopy;
	};

	/**
	 * Handler on application selection change for account type "Shared Calendar / Email Box".
	 * Conditionally enables a dependent application to be selected only when the independent application(s) have been selected.
	 * Removes dependent application from selections if independent application(s) not selected.
	 *
	 * @param {Object[]} itemSelections array of existing selections.
	 * @returns {Object[]} array of updated selections.
	 */
	const onSharedCalendarCheckBoxSelect = itemSelections => {
		let itemSelectionsCopy = [...itemSelections];

		if (
			itemSelectionsCopy.some(
				item => item.Key === '18' || item.Key === '750',
			)
		) {
			if (!sArrApplications.some(item => item.Key === '1482')) {
				setArrApplications(arrApplications => {
					arrApplications.push({
						Key: '1482',
						Value: 'Email Expiration Exemption',
					});
					return arrApplications;
				});
			}
		} else {
			setArrApplications(arrApplications => {
				let indexDependentApp = arrApplications.findIndex(
					app => app.Key === '1482',
				);

				if (indexDependentApp > -1) {
					arrApplications.splice(indexDependentApp, 1);
				}
				return arrApplications;
			});

			// ensure dependent app removed from selections
			const indexDependentApp = itemSelectionsCopy.findIndex(
				app => app.Key === '1482',
			);
			if (indexDependentApp > -1) {
				itemSelectionsCopy.splice(indexDependentApp, 1);
			}
		}

		return itemSelectionsCopy;
	};

	/**
	 * Submission click handler. Constructs payload for server-side processing if form valid.
	 * @returns void
	 */
	const onSubmissionHandler = async () => {
		let validForm = true;

		const ApplicationSelectionsClone = deepCloneObject(
			sArrApplicationSelections,
		);
		var resultsEmail = [];
		ApplicationSelectionsClone.forEach(obj => {
			if (obj.Key === '18' || obj.Key === '750') {
				resultsEmail.push(obj);
			}
		});
		if (resultsEmail.length > 1) {
			setOpenEmailWarningModal(true);
			validForm = false;
		} else {
			validForm = true;
		}
		// Checking form validation
		if (
			sArrApplicationSelections.length > 0 &&
			sJustification.length > 0 &&
			sValidJustification &&
			!sIsLoading &&
			sValidOther &&
			sValidServiceNowGroup &&
			sAreOwnersValid &&
			validForm
		) {
			setIsSubmitting(true);
			axiosInstance
				.post(
					'/api/AddServiceAccount/Submission',
					WrapImsPacket({
						LoggedOnUser: user.ESOID,
						AdditionalOptions: [],
						AccountType: sAccountTypeSelection,
						Justification: sJustification,
						OtherAccountType: sOther,
						CherwellSupportGroup: sServiceNowGroupSelection,
						Applications: sArrApplicationSelections.map(app => {
							return {
								ID: app.Key,
								Name: app.Value,
							};
						}),
						Owners: [
							{
								Cells: {
									Owner_Familiar_ESO_ID: {
										Value: requesteeFamiliarESOID,
									},
									Owner_Email: {
										Value: '',
									},
									Owner_Network_ID: {
										Value: '',
									},
									Service_Account_Network_ID: {
										Value: '',
									},
								},
							},
						],
						RequestType: 'Single',
					}),
				)
				.then(response => {
					setRequestTrackingNumber(
						Object.keys(response.data.payload.WIPHeaderDetails)[0],
					);
				})
				.catch(error => {
					raiseErrorBanner(error, dispatch);
					setIsSubmitting(false);
				});
		}
		setWasSubmitted(true);
	};

	/**
	 * Constructs payload for server-side validation. Payload includes account type and processed owner data.
	 * @returns {Promise<boolean>} indicating validity of payload.
	 */
	const onValidateHandler = async accountTypeSelection => {
		setIsLoading(true);

		let promise = new Promise((resolve, reject) => {
			axiosInstance
				.post(
					'api/AddServiceAccount/Validate',
					WrapImsPacket({
						Owners: {
							AccountType: accountTypeSelection.Key,
							OwnerList: [
								{
									Cells: {
										Owner_Familiar_ESO_ID: {
											Value: requesteeFamiliarESOID,
											Validation: {
												Key: 'false',
											},
										},
										Owner_Email: {
											Value: '',
											Validation: {
												Key: 'true',
											},
										},
										Owner_Network_ID: {
											Value: '',
											Validation: {
												Key: 'true',
											},
										},
										Service_Account_Network_ID: {
											Value: '',
											Validation: {
												Key: 'true',
											},
										},
										ESO_FI_ID: {
											Value: '',
											Validation: {
												Key: 'true',
											},
										},
										Name: {
											Value: '',
											Validation: {
												Key: 'true',
											},
										},
									},
								},
							],
						},
						Applications: sArrApplicationSelections.map(app => {
							return {
								ID: app.Key,
								Name: app.Value,
							};
						}),
						RequestType: 'Single',
					}),
				)
				.then(response => {
					setArrOwners(response.data.payload.OwnerList);
					setAreOwnersValid(response.data.payload.AreValid);
					setExistingUserAccount(
						response.data.payload.ExistingAccount,
					);
					resolve(response.data.payload.AreValid);
				})
				.catch(error => {
					reject(error);
				});
		});
		promise.catch(error => error).finally(() => setIsLoading(false));
		return promise;
	};

	/**
	 * Description associated with the selected account type.
	 */
	let AccountTypeDescriptions = sAccountTypeSelection &&
		sAccountTypeSelection.Description !== undefined &&
		sAccountTypeSelection.Description !== '' && (
			<Fragment>
				<br></br>
				<Subtitle2>
					<b>Description: </b>
					{sAccountTypeSelection.Description}
				</Subtitle2>
			</Fragment>
		);

	/**
	 * Handler to determine the validity of the other textbox.
	 */
	const onOtherHandler = e => {
		if (!e.target.value.match('^(?:[A-Za-z ]|)+$')) {
			setValidOther(false);
		} else {
			setValidOther(true);
		}
		setOther(e.target.value);
	};

	/**
	 * Handler to determine the validity of the justification textbox.
	 */
	const onJustificationHandler = e => {
		if (!e.target.value.match('^(?:[A-Za-z0-9 ,-]|)+$')) {
			setValidJustification(false);
		} else {
			setValidJustification(true);
		}

		setJustification(e.target.value);
	};

	/**
	 * Justification textbox with no special character validation.
	 */
	let justificationInput = (
		<Fragment>
			<Headline6 title='This field is designed for the account requestor to give a brief description (limited to 200 characters), that states the purpose for the account type being requested.'>
				Justification*
			</Headline6>
			<p className={classes[CSS_CLASSES.SUBLABEL]}>
				Char limit: 200 / No special characters: @,#,$,%, etc.
			</p>
			{(!sJustification || sJustification.length < 1) && sWasSubmitted ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					Please enter a justification.
				</Subtitle2>
			) : null}
			<input
				type='text'
				className={CSS_CLASSES.CONTROL}
				value={sJustification}
				onChange={onJustificationHandler}
				maxLength='200'
			/>
			{!sValidJustification ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					No special characters: @,#,$,%, etc.
				</Subtitle2>
			) : null}
		</Fragment>
	);

	/**
	 * Other textbox with no special character validation. Required input
	 */
	let otherInput = sAccountTypeSelection &&
		sAccountTypeSelection.Value === 'Other' && (
			<Fragment>
				<Headline6 title='Please enter the other service account name.'>
					Other
				</Headline6>

				<input
					type='text'
					className={CSS_CLASSES.CONTROL}
					value={sOther}
					onChange={onOtherHandler}
					maxLength='200'
				/>
				{!sValidOther ? (
					<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
						No special characters: @,#,$,%, etc.
					</Subtitle2>
				) : null}
			</Fragment>
		);

	/**
	 * ServiceNow support group dropdown. Required input
	 */
	let serviceNowSupportGroupSelection = sAccountTypeSelection &&
		(sAccountTypeSelection.Key === '45' ||
			sAccountTypeSelection.Key === '5' ||
			sAccountTypeSelection.Key === '75' ||
			sAccountTypeSelection.Key === '79' ||
			sAccountTypeSelection.Key === '83' ||
			sAccountTypeSelection.Key === '84') && (
			<Fragment>
				<Headline6 title='This field is designed for the account requestor to choose an appropriate support team based on the functionality of the service account being requested. Choosing one of these options will allow the correct support team to provide assistance when needed.'>
					ServiceNow Support Group*
				</Headline6>
				{(!sServiceNowGroupSelection ||
					sServiceNowGroupSelection.length < 1) &&
				sWasSubmitted ? (
					<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
						Please select a ServiceNow support group.
					</Subtitle2>
				) : null}
				<select
					className={CSS_CLASSES.CONTROL}
					value={
						sServiceNowGroupSelection
							? sServiceNowGroupSelection.Key
							: ''
					}
					onChange={e => {
						onServiceNowGroupChangeHandler(
							e.target.value
								? {
										Key: e.target.value,
										Value: e.target.selectedOptions[0].text,
									}
								: null,
						);
					}}
				>
					<option key='0' value=''>
						--Select--
					</option>
					{sArrServiceNowGroup.map((serviceNowGroup, index) => (
						<option key={index++} value={serviceNowGroup.key}>
							{serviceNowGroup.value}
						</option>
					))}
				</select>
			</Fragment>
		);

	/**
	 * List of applications associated with the selected account type. Required input
	 */
	let applicationsList = sAccountTypeSelection &&
		sArrApplications.length > 0 && (
			<Fragment>
				<Headline6>Applications*</Headline6>
				{sArrApplicationSelections.length < 1 && sWasSubmitted ? (
					<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
						Please select at least one application.
					</Subtitle2>
				) : null}
				<List
					checkboxList
					className={classes[CSS_CLASSES.CHECKBOX_CONTAINER]}
					selectedIndex={sArrApplicationSelections.map(app =>
						sArrApplications.indexOf(app),
					)}
					handleSelect={indexSelected => {
						setArrApplicationSelections(
							onCheckBoxSelect(
								sArrApplications[indexSelected],
								sArrApplicationSelections,
							),
						);
						setArrOwners([]);
					}}
					xlarge
				>
					{sArrApplications.map(app => {
						const checked =
							sArrApplicationSelections.indexOf(app) > -1;
						return (
							<ListItem key={app.Key}>
								<Checkbox checked={checked} />
								<MDCListItemText primaryText={app.Value} />
							</ListItem>
						);
					})}
				</List>
			</Fragment>
		);

	/**
	 * On submission pop up message.
	 * Based on the account type and taken from the F_Notice field in the TS_User_Type table
	 */
	let noticeModal = sOpenNoticeModal && (
		<Dialog
			open={sOpenNoticeModal}
			onClose={() => setOpenNoticeModal(false)}
		>
			<DialogTitle>Notice</DialogTitle>
			<DialogContent>{sAccountTypeSelection.Notice}</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => setOpenNoticeModal(false)}
				>
					Cancel
				</Button>
				<Button
					variant='text'
					onClick={() => {
						setOpenNoticeModal(false);
						onSubmissionHandler();
					}}
				>
					Proceed
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * On load pop up message.
	 */
	let OnLoadModal = sOpenOnLoadModal && (
		<Dialog
			open={sOpenOnLoadModal}
			onClose={() => setOpenOnLoadModal(false)}
		>
			<DialogTitle>Warning</DialogTitle>
			<DialogContent>
				<p>
					IMS service accounts are not intended for use by someone as
					a substitute for their primary UPMC Network account.
				</p>
				<br></br>
				<p>
					Service accounts can only be used for system access to the
					UPMC Network or shared department mailboxes/calendars.
				</p>
				<br></br>
				<p>
					To request a primary UPMC Network account, click{' '}
					<Link
						to={getLegacyPath(
							'/ESO/NonEmployee_Agreement.aspx?user=0',
						)}
					>
						here
					</Link>
					.
				</p>
			</DialogContent>
			<DialogActions>
				<HomeButton
					onClick={() => setOpenOnLoadModal(false)}
					variant='text'
				>
					Cancel
				</HomeButton>
				<Button
					variant='text'
					onClick={() => setOpenOnLoadModal(false)}
				>
					Continue
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * On submission pop up message.
	 * Based on the email aplications selected.
	 */
	let warningEmailModal = sOpenEmailWarningModal && (
		<Dialog
			open={sOpenEmailWarningModal}
			onClose={() => setOpenEmailWarningModal(false)}
		>
			<DialogTitle>Notice</DialogTitle>
			<DialogContent>
				Both Email and Europe Email applications cannot be requested for
				a single service account. Only one of these applications can be
				associated with this account. To continue, please return and
				remove one of these applications.
			</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => setOpenEmailWarningModal(false)}
				>
					Return
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * Form body.
	 */
	let singleAddForm = !sIsSubmitting && (
		<Fragment>
			{OnLoadModal}
			{noticeModal}
			{warningEmailModal}
			<Headline6>Account Type*</Headline6>
			{sAccountTypeSelection && sExistingUserAccount ? (
				<Subtitle2 className={classes[CSS_CLASSES.INVALID]}>
					User already has selected account type or a recently retired account. Existing account:{' '}
					{sExistingUserAccount}
				</Subtitle2>
			) : null}
			<select
				className={CSS_CLASSES.CONTROL}
				value={sAccountTypeSelection ? sAccountTypeSelection.Key : ''}
				onChange={e => {
					onAccountTypeChangeHandler(
						e.target.value
							? {
									Key: e.target.value,
									Value: e.target.selectedOptions[0].text,
									Description:
										e.target.selectedOptions[0].attributes
											.description.value,
									Notice: e.target.selectedOptions[0]
										.attributes.notice.value,
								}
							: null,
					);
				}}
			>
				<option key='0' value=''>
					--Select--
				</option>
				{sArrAccountType.map((accountType, index) => (
					<option
						key={index++}
						value={accountType.key}
						description={accountType.description}
						notice={accountType.notice}
					>
						{accountType.value}
					</option>
				))}
			</select>
			{AccountTypeDescriptions}
			{otherInput}
			{serviceNowSupportGroupSelection}
			{justificationInput}
			{applicationsList}
			<Actions className={classes[CSS_CLASSES.ACTIONS]}>
				<Button
					variant='contained'
					onClick={() => {
						sAccountTypeSelection.Description &&
						sAccountTypeSelection.Notice
							? setOpenNoticeModal(true)
							: onSubmissionHandler();
					}}
					disabled={!sAccountTypeSelection || sIsLoading}
				>
					Submit
				</Button>
			</Actions>
		</Fragment>
	);

	// Redirect request to app pages when the submission is complete
	return sRequestTrackingNumber ? (
		<div>
			<Redirect
				to={
					'/RRS/Request_ModifyDetail_View.aspx?tn=' +
					sRequestTrackingNumber +
					'&REQUEST_TYPE=' +
					(requestType === 'For_Myself'
						? RequestTypes.SELF_SERVICE_ACCOUNT
						: RequestTypes.FOR_OTHERS_SERVICE_ACCOUNT)
				}
			/>
		</div>
	) : (
		<Base
			title='New Service Account Creation'
			helpLink={
				'https://infonet.upmc.com/infonet/ls/content/3298180640090487/service-account-creation-and-management-pdf-1764800010'
			}
			loading={sIsLoading || sIsSubmitting}
		>
			<Card xlarge className={classes[CSS_CLASSES.TITLE_BODY]}>
				<Caption>
					<Body1>
						Requesting for: <br></br>
						<b>
							<div
								className={classes[CSS_CLASSES.USER_NETWORK_ID]}
							>
								{UserName}
								<br></br>
							</div>
						</b>
					</Body1>
					<ol className={classes[CSS_CLASSES.TITLE_BODY_LIST]}>
						<div>
							<li
								className={
									classes[
										CSS_CLASSES.TITLE_BODY_LIST_NO_INDENT
									]
								}
							>
								To submit request for service account creation:
							</li>
							<li>
								Select <b>Account Type</b> and associated{' '}
								<b>Applications</b>.
							</li>
							<li>
								Press <b>Submit</b> to proceed with request.
							</li>
						</div>
					</ol>
				</Caption>
			</Card>
			{singleAddForm}
		</Base>
	);
};

export default AddServiceAccount;
